import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Contract } from '../../models/Contract'
import { RecordFactory } from '../../models/RecordFactory'
import { Signature } from '../../models/Signature'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from '../items/ContractSearchableSelect'
import { PDFPreviewModal } from '../common/PDFPreviewModal'
import { Chip } from '../common/fields/Chip'
import { WorkflowPage } from '../workflows/WorkflowPage'

const render = ({ base: { me, logAndDisplayError } }: PocketComponentProps<void>) => {
    const navigate = useNavigate()

    const [client, setClient] = useState<User | undefined>(undefined)
    const [subaccount, setSubaccount] = useState<Subaccount | undefined>(undefined)
    const [contract, setContract] = useState<Contract | undefined>(undefined)
    const [channel, setChannel] = useState<string>('sms')

    const [isLoading, setIsLoading] = useState(false)
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const handleSelectClient = (client: User | undefined) => {
        setClient(client)

        if (client === undefined) {
            return
        }

        if (!client.phone && channel === 'sms') {
            setChannel('email')
        }
        else if (!client.email && channel === 'email') {
            setChannel('sms')
        }
    }

    const handleSend = async () => {
        if (client === undefined || contract === undefined) {
            return
        }

        const signature = new Signature({
            contract,
            employee: me,
            person: client,
            subaccount,
            channel,
        })

        setIsLoading(true)
        const result = await RecordFactory.getInstance().sendSignature(signature, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            setIsLoading(false)
            return
        }

        navigate(`/messages/${signature.person?.id}`, { replace: true })
    }

    return (
        <WorkflowPage
            title="Send contract"
            onClose={{
                action: 'go-back',
                excludePathname: /^\/contracts\/send$/,
                defaultPathname: '/dashboards/contracts',
            }}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="secondary"
                        disabled={contract === undefined}
                        onClick={() => setIsPreviewModalOpen(true)}
                    >
                        Preview
                    </Button>

                    <Button
                        intent="primary"
                        disabled={client === undefined || contract === undefined}
                        isLoading={isLoading}
                        onClick={handleSend}
                    >
                        Send
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General" className="w-full">
                    <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                        <ClientSearchableSelect
                            mode="server"
                            selectedClient={client}
                            selectedSubaccount={subaccount}
                            onSelectClient={handleSelectClient}
                            onSelectSubaccount={(subaccount) => {
                                setClient(subaccount?.client)
                                setSubaccount(subaccount)
                            }}
                            shouldPreserveLastSelection={true}
                            canAddNew
                            shouldIncludeSubaccounts
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={contract}
                            onSelectContract={(contract) => setContract(contract)}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Send via"
                        icon="messageArrowUp.large"
                        description="Specify how you want this contract to be sent."
                    >
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                            <Chip
                                title="Text message"
                                isSelected={channel === 'sms'}
                                onClick={() => setChannel('sms')}
                                size="small"
                            />
                            <Chip
                                title="Text and email"
                                isSelected={channel === 'combo'}
                                onClick={() => setChannel('combo')}
                                size="small"
                            />
                            <Chip
                                title="Email"
                                isSelected={channel === 'email'}
                                onClick={() => setChannel('email')}
                                size="small"
                            />
                        </div>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <PDFPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                endpoint={{ path: `/contract/${contract?.id}/print` }}
            />
        </WorkflowPage>
    )
}
export const SendContract = usePSComponent(render)
